import React from "react";
import { useTranslation } from "react-i18next";
import { MessageCircle, X, Send, Loader2, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Persistent per-browser conversation id */
function getSessionId() {
  let sid = localStorage.getItem("eif_ai_sid");
  if (!sid) {
    sid = `chat_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem("eif_ai_sid", sid);
  }
  return sid;
}

function Message({ role, text }) {
  const isUser = role === "user";
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`} data-testid={`ai-msg-${role}`}>
      <div className={`max-w-[85%] rounded-2xl px-3.5 py-2 text-sm leading-relaxed whitespace-pre-wrap ${
        isUser
          ? "bg-[color:var(--forest)] text-white rounded-br-sm"
          : "bg-[color:var(--forest-soft)] text-[color:var(--ink)] rounded-bl-sm"
      }`}>
        {text || <span className="opacity-60">…</span>}
      </div>
    </div>
  );
}

export default function ConciergeChat() {
  const { i18n, t } = useTranslation();
  const [open, setOpen] = React.useState(false);
  const [messages, setMessages] = React.useState([]);
  const [input, setInput] = React.useState("");
  const [streaming, setStreaming] = React.useState(false);
  const scrollRef = React.useRef(null);
  const abortRef = React.useRef(null);
  const sid = React.useMemo(() => getSessionId(), []);

  React.useEffect(() => {
    if (!open || messages.length) return;
    fetch(`${API}/ai/concierge/history?session_id=${encodeURIComponent(sid)}`)
      .then((r) => r.json())
      .then((docs) => {
        if (Array.isArray(docs) && docs.length) {
          setMessages(docs.map((d) => ({ role: d.role, text: d.content })));
        } else {
          setMessages([{
            role: "assistant",
            text: {
              en: "Hi! I'm your EventHub Concierge. Ask me anything about the agenda — sessions, speakers, times, or where to be.",
              fr: "Bonjour ! Je suis votre concierge EventHub. Posez-moi vos questions sur l'agenda — sessions, intervenants, horaires.",
              pt: "Olá! Sou o seu concierge EventHub. Pergunte-me sobre a agenda — sessões, oradores, horários.",
              ar: "مرحباً! أنا كونسيرج EventHub. اسألني عن الأجندة — الجلسات، المتحدثون، الأوقات.",
            }[i18n.language] || "Hi! I'm your EventHub Concierge.",
          }]);
        }
      }).catch(() => {});
  }, [open, sid, i18n.language, messages.length]);

  React.useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, streaming]);

  const send = async () => {
    const text = input.trim();
    if (!text || streaming) return;
    setMessages((m) => [...m, { role: "user", text }, { role: "assistant", text: "" }]);
    setInput("");
    setStreaming(true);
    abortRef.current = new AbortController();
    try {
      const res = await fetch(`${API}/ai/concierge`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sid, message: text, language: i18n.language }),
        signal: abortRef.current.signal,
      });
      if (!res.ok || !res.body) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let assistantText = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let idx;
        while ((idx = buffer.indexOf("\n\n")) !== -1) {
          const raw = buffer.slice(0, idx).trim();
          buffer = buffer.slice(idx + 2);
          if (!raw.startsWith("data:")) continue;
          try {
            const evt = JSON.parse(raw.slice(5).trim());
            if (evt.delta) {
              assistantText += evt.delta;
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", text: assistantText };
                return copy;
              });
            } else if (evt.error) {
              assistantText += `\n\n⚠ ${evt.error}`;
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", text: assistantText };
                return copy;
              });
            }
          } catch (err) {
            // Malformed SSE chunk during streaming — swallow and keep reading.
            // Logged at debug level so dev console shows cause but UX is unaffected.
            if (typeof console !== "undefined" && console.debug) {
              console.debug("[concierge] sse chunk parse failed:", err?.message || err);
            }
          }
        }
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", text: `⚠ ${e.message || "Chat failed"}` };
        return copy;
      });
    } finally {
      setStreaming(false);
      abortRef.current = null;
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
  };

  const isRTL = i18n.language === "ar";

  return (
    <>
      {/* Floating action button */}
      {!open && (
        <button
          data-testid="ai-fab"
          onClick={() => setOpen(true)}
          className="fixed bottom-5 end-5 z-40 w-14 h-14 rounded-full bg-[color:var(--forest)] text-white shadow-lg flex items-center justify-center hover:bg-[color:var(--forest-dark)] transition-all"
          style={{ boxShadow: "0 10px 30px -8px rgba(31,107,62,0.5)" }}
          aria-label="Open concierge chat"
        >
          <MessageCircle className="w-6 h-6" />
          <span className="absolute -top-1 -end-1 w-4 h-4 bg-[color:var(--amber)] rounded-full flex items-center justify-center">
            <Sparkles className="w-2.5 h-2.5 text-white" />
          </span>
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div className="fixed inset-x-0 bottom-0 sm:inset-x-auto sm:bottom-5 sm:end-5 z-40 sm:w-[380px] max-h-[85vh] sm:max-h-[560px] flex flex-col bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl border border-[color:var(--line)] overflow-hidden animate-fade-up"
             data-testid="ai-panel" dir={isRTL ? "rtl" : "ltr"}>
          {/* Header */}
          <div className="px-4 py-3 bg-[color:var(--forest)] text-white flex items-center justify-between">
            <div>
              <div className="font-bold text-sm flex items-center gap-1.5">
                <Sparkles className="w-4 h-4 text-[color:var(--amber-soft)]" />
                EventHub Concierge
              </div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-white/70">Claude · Sonnet 4.6</div>
            </div>
            <button data-testid="ai-close" onClick={() => setOpen(false)} className="p-1 rounded hover:bg-white/10">
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-4 space-y-2.5 bg-[color:var(--cream)]" data-testid="ai-messages">
            {messages.map((m, i) => <Message key={i} role={m.role} text={m.text} />)}
            {streaming && (
              <div className="flex justify-start">
                <div className="bg-[color:var(--forest-soft)] rounded-2xl rounded-bl-sm px-3.5 py-2 text-[color:var(--forest-dark)] flex items-center gap-1.5">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  <span className="text-xs">Thinking…</span>
                </div>
              </div>
            )}
          </div>

          {/* Composer */}
          <div className="p-3 border-t border-[color:var(--line)] bg-white flex gap-2">
            <input
              data-testid="ai-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKey}
              placeholder={{
                en: "Ask about the agenda…",
                fr: "Posez une question sur l'agenda…",
                pt: "Pergunte sobre a agenda…",
                ar: "اسأل عن الأجندة…",
              }[i18n.language] || "Ask about the agenda…"}
              maxLength={600}
              disabled={streaming}
              className="flex-1 rounded-full border border-[color:var(--line-strong)] px-4 py-2 text-sm focus:outline-none focus:border-[color:var(--forest)]"
            />
            <button
              data-testid="ai-send"
              onClick={send}
              disabled={!input.trim() || streaming}
              className="w-10 h-10 rounded-full bg-[color:var(--forest)] text-white flex items-center justify-center disabled:opacity-40 hover:bg-[color:var(--forest-dark)]"
              aria-label="Send"
            >
              {streaming ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
